import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RootState } from '@/src/store/rootReducer'
import { IBranding, IMeetCodes, IQuestionSection, ISettings } from '@/src/types/meetData.type'
import { initialBranding } from '@/src/constants/branding'

export interface IMeetDataState {
  roomName: string
  prompt: string
  questionSections: IQuestionSection[]

  isAdmin: boolean

  meetCodes: IMeetCodes | null
  branding: IBranding
  settings: ISettings
}

const initialSettings: ISettings = {
  auto_rec: false,
  gpt_protocol: false,
  transcript: false,
  wait_room: false,
  is_join_sound_enabled: true,
  split_rooms: false,
}

export const initialMeetData: IMeetDataState = {
  roomName: '',
  prompt: '',
  questionSections: [],
  isAdmin: false,
  meetCodes: null,
  branding: initialBranding,
  settings: initialSettings,
}

const meetDataAdapter = createEntityAdapter()

export const meetDataSlice = createSlice({
  name: 'meetData',
  initialState: meetDataAdapter.getInitialState(initialMeetData),
  reducers: {
    setMeetData: (state, action: PayloadAction<IMeetDataState>) => ({
      ...state,
      ...action.payload,
    }),
    setRoomName: (state, action: PayloadAction<string>) => {
      state.roomName = action.payload
    },
    setPrompt: (state, action: PayloadAction<string>) => {
      state.prompt = action.payload
    },
    setQuestionSections: (state, action: PayloadAction<IQuestionSection[]>) => {
      state.questionSections = action.payload
    },
    setSettings: (state, action: PayloadAction<ISettings>) => {
      state.settings = action.payload
    },
    setBranding: (state, action: PayloadAction<IBranding>) => {
      state.branding = action.payload
    },
    setMeetCodes: (state, action: PayloadAction<IMeetCodes>) => {
      state.meetCodes = action.payload
    },
    setIsAdmin: (state, action: PayloadAction<boolean>) => {
      state.isAdmin = action.payload
    },
  },
})

export const {
  setMeetData,
  setRoomName,
  setPrompt,
  setQuestionSections,
  setIsAdmin,
  setSettings,
  setMeetCodes,
  setBranding,
} = meetDataSlice.actions

export const { selectAll: meetDataSelector } = meetDataAdapter.getSelectors(
  (state: RootState) => state[meetDataSlice.name],
)

export const selectMeetData = (state: RootState) => state.meetData
export const selectRoomName = (state: RootState) => state.meetData.roomName
export const selectPrompt = (state: RootState) => state.meetData.prompt
export const selectQuestionSections = (state: RootState) => state.meetData.questionSections
export const selectSettings = (state: RootState) => state.meetData.settings
export const selectMeetCodes = (state: RootState) => state.meetData.meetCodes
export const selectIsAdmin = (state: RootState) => state.meetData.isAdmin
export const selectBranding = (state: RootState) => state.meetData.branding
export const selectSplitRooms = (state: RootState) => state.meetData.settings.split_rooms ?? false
